import { createI18n } from 'vue-i18n'

import {
  getAppMetadataLanguages,
  getAppMetadataLocalStorageKeys,
} from './plan/metadata/metadata'

function loadLocaleMessages(): Record<string, unknown> {
  // `import: 'default'` is required: the bare module namespace only exposes
  // identifier-safe keys as named exports, silently dropping keys with spaces
  // (e.g. 'United Kingdom') from the messages intlify can resolve.
  const locales = import.meta.glob('./locales/*.json', {
    eager: true,
    import: 'default',
  })
  const messages: Record<string, unknown> = {}
  Object.entries(locales).forEach(([key, value]) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i)
    if (matched && matched.length > 1) {
      const locale = matched[1]
      messages[locale] = value
    }
  })
  return messages
}

function getBrowserLocale(): string {
  return (
    navigator.languages && navigator.languages.length
      ? navigator.languages[0]
      : navigator.language ||
        (navigator as Navigator & { userLanguage?: string }).userLanguage ||
        'en'
  ).toLowerCase()
}

function getMetadataLocales(): string[] {
  const locales = getAppMetadataLanguages().options.reduce<string[]>(
    (acc, locale) => {
      acc.push(locale.id.toLowerCase())
      if (locale.locale) {
        acc.push(locale.locale.toLowerCase())
      }
      return acc
    },
    [],
  )
  // NOTE: hack to remove es locale from metadata locales
  // We no longer have the 'es' language in the application, but it's still in the metadata file due to testing. That's why we removed it with the filter.
  // This should be removed in the future when we adapt the tests.
  // In the 'localeMixin.js' file, we also filter the 'es' language.
  return [...new Set(locales)].filter((locale) => locale !== 'es')
}

function loadLocale(): string {
  let browserLocale: string | null = getBrowserLocale()
  console.debug('🇳🇵 Browser locale: ', browserLocale)
  const metaLocales = getMetadataLocales()
  console.debug('🇳🇵 Metadata locales: ', metaLocales)
  if (!metaLocales.includes(browserLocale)) {
    browserLocale = null
  }
  const locale =
    window.localStorage.getItem(getAppMetadataLocalStorageKeys().lang) ||
    import.meta.env.VITE_I18N_LOCALE ||
    browserLocale ||
    getAppMetadataLanguages().options[0].locale ||
    getAppMetadataLanguages().options[0].id ||
    'en'
  console.debug('🇳🇵 Locale selected: ', locale)
  return locale
}

// Composition-API mode (no legacy VueI18n instance). Now that every i18n-using
// component is `<script setup>`, a bare `useI18n()` resolves to the global scope
// and templates get `$t` / `v-t` via `globalInjection: true`. This also removes
// vue-i18n's legacy `beforeCreate` mixin, which threw a caught
// `'set' on proxy … '$t'` on every Composition-API component that aliases
// `const { t: $t } = useI18n()`. Mirrors the c3/fi/axion end state.
// `missingWarn` surfaces typos/untranslated keys; call sites must resolve
// empty/absent values to '' WITHOUT calling the translator.
export default createI18n({
  legacy: false,
  globalInjection: true,
  locale: loadLocale(),
  fallbackLocale: import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'en',
  // createI18n's messages generic is too strict for dynamically-globbed JSON
  // locales; the runtime shape is unchanged from the working setup.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messages: loadLocaleMessages() as any,
  missingWarn: true,
  fallbackWarn: false,
})
